import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  FaPhone, FaEnvelope, FaMapMarkerAlt, FaShoppingCart,
  FaBatteryFull, FaWrench, FaTachometerAlt, FaCheckCircle,
  FaStar, FaChevronRight, FaInfoCircle, FaDollarSign,
  FaCalendarAlt, FaIdCard, FaShieldAlt, FaCreditCard
} from 'react-icons/fa';
import { FiPhone, FiArrowLeft } from 'react-icons/fi';
import { vehicleService } from '../services/vehicleService';
import { formatVehicleTitle } from '../utils/textFormatting';
import { canonicalizeUrl } from '../utils/canonicalUrl';
import PaymentModal from '../components/PaymentModal';
import LeadForm from '../components/LeadForm';
import ImageGallery from '../components/ImageGallery';
import { sampleGolfCarts } from '../data/sampleGolfCarts';

const GolfCartDetail = () => {
  
  const { id } = useParams();
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentType, setPaymentType] = useState('deposit');
  const [showLeadForm, setShowLeadForm] = useState(false);

  // Load golf cart data
  useEffect(() => {
    window.scrollTo(0, 0);
    loadGolfCart();
  }, [id]);

  const loadGolfCart = async () => {
    try {
      setLoading(true);
      const { data, error } = await vehicleService.getVehicleById(id);
      
      if (error || !data) {
        // Fallback to sample golf carts if not found in database
        const sampleCart = sampleGolfCarts.find(c => c.id === parseInt(id));
        if (sampleCart) {
          // Transform sample data to match expected format
          setCart({
            ...sampleCart,
            make: sampleCart.brand,
            images: sampleCart.image ? [sampleCart.image] : [],
            seating: sampleCart.passengers,
            vehicleType: 'golf-cart'
          });
        } else {
          setError('Golf cart not found');
        }
      } else {
        // Verify this is actually a golf cart
        const vehicleType = (data.vehicleType || data.vehicle_type || '').toLowerCase();
        if (vehicleType.includes('golf') || vehicleType.includes('cart')) {
          setCart(data);
        } else {
          setCart(data); // Still show it since they came from golf carts page
        }
      }
    } catch (err) {
      console.error('Error loading golf cart:', err);
      // Fallback to sample golf carts on exception
      const sampleCart = sampleGolfCarts.find(c => c.id === parseInt(id));
      if (sampleCart) {
        setCart({
          ...sampleCart,
          make: sampleCart.brand,
          images: sampleCart.image ? [sampleCart.image] : [],
          seating: sampleCart.passengers,
          vehicleType: 'golf-cart'
        });
      } else {
        setError('Failed to load golf cart details');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !cart) {
    return (
      <div className="min-h-screen bg-gray-50 py-10">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-xl mx-auto">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Golf Cart Not Found</h1>
            <p className="text-gray-600 mb-6">{error || 'We couldn\'t find the golf cart you\'re looking for.'}</p>
            <Link to="/golf-carts" className="inline-flex items-center gap-2 bg-gray-900 text-white px-6 py-3 rounded-lg">
              <FiArrowLeft /> Back to Golf Carts
            </Link>
          </div>
        </div>
      </div>
    );
  }

  
  return (
    <div className="min-h-screen bg-gray-50">
      {cart && (
        <Helmet>
          <title>{`${cart.year || ''} ${cart.make || cart.brand || ''} ${cart.model || ''} Golf Cart for Sale in Paradise, TX | Paradise-Performance`}</title>
          <meta name="description" content={`Buy this ${cart.condition} ${cart.year} ${cart.make || cart.brand} ${cart.model} golf cart for $${cart.price?.toLocaleString()}. ${cart.seats || cart.passengers} passenger seating, ${cart.power_type === 'gas' ? 'gas powered' : 'electric'}. Available in Paradise, TX. Financing options.`} />
          
          {/* Open Graph / Facebook */}
          <meta property="og:type" content="product" />
          <meta property="og:url" content={canonicalizeUrl(`/golf-cart/${cart.id}`)} />
          <meta property="og:title" content={`${cart.year} ${cart.make || cart.brand} ${cart.model} - $${cart.price?.toLocaleString()}`} />
          <meta property="og:description" content={`${cart.seats || cart.passengers} passengers • ${cart.power_type === 'gas' ? 'Gas' : 'Electric'} • ${cart.condition}`} />
          <meta property="og:image" content={cart.images?.[0] || cart.image} />
          <meta property="product:price:amount" content={cart.price} />
          <meta property="product:price:currency" content="USD" />
          
          {/* Twitter */}
          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:title" content={`${cart.year} ${cart.make || cart.brand} ${cart.model} Golf Cart`} />
          <meta property="twitter:description" content={`For Sale: $${cart.price?.toLocaleString()} • ${cart.seats || cart.passengers} passengers`} />
          <meta property="twitter:image" content={cart.images?.[0] || cart.image} />
          
          {/* Canonical URL */}
          <link rel="canonical" href={canonicalizeUrl(`/golf-cart/${cart.id}`)} />
          
          {/* Structured Data - Product Schema (Google Shopping compliant) */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Product",
              "name": `${cart.year || ''} ${cart.make || cart.brand || ''} ${cart.model || ''} Golf Cart`.trim(),
              "brand": {
                "@type": "Brand",
                "name": cart.make || cart.brand || 'Golf Cart'
              },
              "model": cart.model,
              "sku": cart.stockNumber || cart.vin || String(cart.id),
              "productID": cart.vin || cart.stockNumber || String(cart.id),
              "url": `https://paradise-performance.com/golf-cart/${cart.id}`,
              "category": "Vehicles & Parts > Vehicles > Golf Carts",
              "description": cart.description || `${cart.condition || 'Pre-owned'} ${cart.year || ''} ${cart.make || cart.brand || ''} ${cart.model || ''} golf cart with ${cart.seating || cart.seats || cart.passengers || '2'} passenger seating. Available at Paradise-Performance in Paradise, TX.`,
              "image": (cart.images && cart.images.length > 0) ? cart.images : (cart.image ? [cart.image] : []),
              ...(cart.exteriorColor ? { "color": cart.exteriorColor } : {}),
              "itemCondition": (cart.condition || '').toLowerCase() === 'new' ? 'https://schema.org/NewCondition' : 'https://schema.org/UsedCondition',
              "offers": {
                "@type": "Offer",
                "priceCurrency": "USD",
                "price": cart.price,
                "url": `https://paradise-performance.com/golf-cart/${cart.id}`,
                "availability": (!cart.status || cart.status.toLowerCase() !== 'sold') ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
                "itemCondition": (cart.condition || '').toLowerCase() === 'new' ? 'https://schema.org/NewCondition' : 'https://schema.org/UsedCondition',
                "seller": {
                  "@type": "AutoDealer",
                  "name": "Paradise-Performance LLC",
                  "address": {
                    "@type": "PostalAddress",
                    "streetAddress": "305 Co Rd 3470",
                    "addressLocality": "Paradise",
                    "addressRegion": "TX",
                    "postalCode": "76073"
                  },
                  "telephone": "(817) 382-2316"
                }
              }
            })}
          </script>
        </Helmet>
      )}
      <div className="container mx-auto px-4 py-8">
        <nav className="mb-6">
          <ol className="flex items-center space-x-2 text-sm text-gray-500">
            <li><Link to="/" className="hover:text-primary-600">Home</Link></li>
            <li>/</li>
            <li><Link to="/golf-carts" className="hover:text-primary-600">Golf Carts</Link></li>
            <li>/</li>
            <li className="text-gray-900">{cart.make} {cart.model}</li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
            <ImageGallery images={cart.images || []} videos={cart.videos || []} />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{formatVehicleTitle(cart)}</h1>
            <p className="text-gray-600 mb-4">
              {cart.year} • 
              {cart.seating ? `${cart.seating} Passengers` : ''} • 
              {cart.topSpeed ? cart.topSpeed : ''} • 
              {cart.range ? cart.range : ''}
            </p>
            {cart.description && (
              <p className="text-gray-700 leading-relaxed">{cart.description}</p>
            )}
            {Array.isArray(cart.features) && cart.features.length > 0 && (
              <div className="mt-6">
                <h2 className="text-xl font-semibold mb-2">Key Features</h2>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  {cart.features.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                ${(cart.price || 0).toLocaleString()}
              </h2>
              <p className="text-gray-600 mb-6">Plus taxes & fees</p>
              <div className="space-y-3">
                <button 
                  onClick={() => {
                    setPaymentType('full');
                    setShowPaymentModal(true);
                  }}
                  className="w-full bg-primary-600 text-white py-3 rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center gap-2">
                  <FaDollarSign /> Purchase
                </button>
                <button 
                  onClick={() => {
                    setPaymentType('deposit');
                    setShowPaymentModal(true);
                  }}
                  className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2">
                  <FaCreditCard /> Make a Deposit
                </button>
                <button 
                  onClick={() => setShowLeadForm(true)}
                  className="w-full border border-gray-300 py-3 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2">
                  <FaEnvelope /> Contact Us
                </button>
                <a href="tel:8173822316" className="flex items-center justify-center gap-2 w-full bg-gray-800 text-white py-3 rounded-lg hover:bg-gray-900 transition-colors">
                  <FiPhone /> (817) 382-2316
                </a>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold mb-3">Details</h3>
              <div className="grid grid-cols-2 gap-3 text-sm text-gray-700">
                <div>
                  <div className="text-gray-500">Condition</div>
                  <div className="font-medium capitalize">{cart.condition || 'Used'}</div>
                </div>
                {cart.year && (
                  <div>
                    <div className="text-gray-500">Year</div>
                    <div className="font-medium">{cart.year}</div>
                  </div>
                )}
                {cart.seating && (
                  <div>
                    <div className="text-gray-500">Seating</div>
                    <div className="font-medium">{cart.seating} Passengers</div>
                  </div>
                )}
                {cart.exteriorColor && (
                  <div>
                    <div className="text-gray-500">Color</div>
                    <div className="font-medium">{cart.exteriorColor}</div>
                  </div>
                )}
                {cart.stockNumber && (
                  <div>
                    <div className="text-gray-500">Stock #</div>
                    <div className="font-medium">{cart.stockNumber}</div>
                  </div>
                )}
                {cart.vin && (
                  <div>
                    <div className="text-gray-500">VIN / Serial</div>
                    <div className="font-medium flex items-center gap-2">
                      <span className="font-mono break-all">{cart.vin}</span>
                      <button
                        type="button"
                        onClick={() => navigator.clipboard?.writeText(cart.vin)}
                        className="text-primary-600 hover:text-primary-700 text-xs"
                        title="Copy to clipboard"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                )}
                {cart.batteryType && (
                  <div>
                    <div className="text-gray-500">Battery Type</div>
                    <div className="font-medium capitalize">{cart.batteryType}</div>
                  </div>
                )}
              </div>
            </div>

            <Link to="/golf-carts" className="inline-flex items-center gap-2 text-primary-600 font-medium">
              <FiArrowLeft /> Back to Golf Carts
            </Link>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          vehicle={{
            id: cart.id,
            year: cart.year,
            make: cart.make,
            model: cart.model,
            price: cart.price
          }}
          paymentType={paymentType}
          maxAmount={5000}
        />
      )}

      {/* Lead Form Modal */}
      {showLeadForm && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">Contact Us About This Golf Cart</h2>
              <button
                onClick={() => setShowLeadForm(false)}
                className="text-gray-500 hover:text-gray-700 text-3xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              <LeadForm
                vehicleInfo={{
                  year: cart?.year || 2024,
                  make: cart?.make || 'Golf Cart',
                  model: cart?.model || 'Model',
                  vin: cart?.vin || cart?.id || 'N/A',
                  price: cart?.price || 0,
                  type: 'golf-cart'
                }}
                leadType="test_drive"
                onClose={() => setShowLeadForm(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GolfCartDetail;
